import { SidebarSection } from './common';
import {
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import { useTabManager } from '../../tab-manager/tab-manager';
import { Routes } from '../../routes';
import { SideBarSection } from './types';
import { NewDocumentAction } from '../../document/new-document.action';
import { DeleteDocumentAction } from '../../document/delete-document.action';
import { RenameDocumentAction } from '../../document/rename-document.action';
import { DuplicateAction } from '../../graph/duplicate.action';
import { DG } from '@cotera/sdk/core';
import { DocumentModel } from '../../document/document.model';
import { Assert, using } from '@cotera/utilities';
import { DisposableTargetedModelContext } from '../../actions/etc';
import { v4 } from 'uuid';
import { IconName } from '@cotera/client/app/components/ui/icon';

export const Documents: React.FC = () => {
  const tabManager = useTabManager();
  const graphModel = useInScopeModel(GraphModel);
  const graph = useWatchableValue(graphModel!.graph);
  const newDocumentAction = useAction(NewDocumentAction);
  const deleteDocumentAction = useAction(DeleteDocumentAction);
  const renameDocumentAction = useAction(RenameDocumentAction);
  const duplicateAction = useAction(DuplicateAction);
  const context = useActionsContext();

  // Get all documents from the graph
  const documents: Array<{ name: string; node: DG.Graph.DocumentNode }> = [];

  Object.entries(graph.nodeMap).forEach(([symbol, node]) => {
    if (node.t === 'document') {
      documents.push({ name: symbol, node });
    }
  });

  const documentsSection: SideBarSection = {
    title: 'Documents',
    items: documents.map(({ name }) => {
      const quickActions = [
        {
          icon: 'pencil' as IconName,
          label: 'Rename Document',
          onClick: async () => {
            Assert.assert(graphModel !== null);

            // Create a temporary DocumentModel for this document
            const documentModel = new DocumentModel(
              {
                name,
                graph: graphModel.graph,
                compiler: graphModel.compiler,
              },
              {
                id: v4(),
                targeted: true,
                isNew: false,
              }
            );

            await using(
              new DisposableTargetedModelContext(context, documentModel),
              async () => {
                await renameDocumentAction.execute({});
              }
            );
          },
          variant: 'grouped' as const,
        },
        {
          icon: 'trash' as IconName,
          label: 'Delete Document',
          onClick: async () => {
            Assert.assert(graphModel !== null);

            // Create a temporary DocumentModel for this document
            const documentModel = new DocumentModel(
              {
                name,
                graph: graphModel.graph,
                compiler: graphModel.compiler,
              },
              {
                id: v4(),
                targeted: true,
                isNew: false,
              }
            );

            await using(
              new DisposableTargetedModelContext(context, documentModel),
              async () => {
                await deleteDocumentAction.execute({});
              }
            );
          },
          variant: 'grouped' as const,
        },
        {
          icon: 'copy' as IconName,
          label: 'Duplicate Document',
          onClick: async () => {
            Assert.assert(graphModel !== null);

            // Create a temporary DocumentModel for this document
            const documentModel = new DocumentModel(
              {
                name,
                graph: graphModel.graph,
                compiler: graphModel.compiler,
              },
              {
                id: v4(),
                targeted: true,
                isNew: false,
              }
            );

            await using(
              new DisposableTargetedModelContext(context, documentModel),
              async () => {
                await duplicateAction.execute();
              }
            );
          },
          variant: 'grouped' as const,
        },
      ];

      return {
        t: 'item' as const,
        title: name,
        icon: 'document-text' as const,
        onClick: () => {
          tabManager.push({
            route: Routes.document.forId(name).toString(),
            title: name,
            displayTitle: name,
            icon: 'document-text',
          });
        },
        quickActions,
      };
    }),
    quickActions: [
      {
        icon: 'plus',
        label: 'New Document',
        onClick: async () => {
          await newDocumentAction.execute();
        },
      },
    ],
  };

  return <SidebarSection section={documentsSection} />;
};
